/**
 * Screen reads that a background warm can fill before the screen opens, so it
 * draws at once and refreshes behind the content. Every entry is keyed by the
 * signed-in actor and workspace and only ever returned for that pair; entries
 * are remembered on this device for a day so a reopened app draws them before
 * the network answers. Sign-out clears them all.
 */
import { storedSnapshotOwner } from './dataService/snapshotCache';

export interface BackgroundReadScope { actorId: string; workspaceId: string }
interface Entry { actorId: string; workspaceId: string; name: string; at: number; data: unknown }

const PREFIX = 'quantis-background-read:v1:';
const KEEP_MS = 24 * 60 * 60_000;
const LIMIT = 32;
// Large registers belong to their own stores, and the /database snapshot needs
// the storage quota more; these are short "coming up" style lists.
const MAX_STORED_CHARS = 100_000;
const entries = new Map<string, Entry>();
const requests = new Map<string, Promise<unknown>>();
let generation = 0;

export const backgroundReadKey = (scope: BackgroundReadScope, name: string) =>
  `${PREFIX}${JSON.stringify([scope.actorId, scope.workspaceId, name])}`;

/** The signed-in session's own scope, or null while no account owns this device. */
export function currentBackgroundReadScope(workspaceId: string | null | undefined): BackgroundReadScope | null {
  const actorId = storedSnapshotOwner();
  return actorId && workspaceId ? { actorId, workspaceId } : null;
}

function storage(): Storage | null {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
}

// Storage.key() rather than Object.keys(): not every Storage exposes its items as properties.
function storedKeys(local: Storage): string[] {
  const keys: string[] = [];
  for (let index = 0; index < local.length; index++) {
    const key = local.key(index);
    if (key?.startsWith(PREFIX)) keys.push(key);
  }
  return keys;
}

function usable(entry: Entry | null | undefined, scope: BackgroundReadScope, name: string): entry is Entry {
  return !!entry && entry.actorId === scope.actorId && entry.workspaceId === scope.workspaceId && entry.name === name
    && typeof entry.at === 'number' && Number.isFinite(entry.at) && entry.at <= Date.now() && Date.now() - entry.at < KEEP_MS;
}

/** The last confirmed answer for this scope and its age, without a request. */
export function peekBackgroundRead<T>(scope: BackgroundReadScope | null, name: string): { data: T; at: number } | undefined {
  if (!scope) return undefined;
  const key = backgroundReadKey(scope, name);
  let entry = entries.get(key);
  if (!entry) {
    try { entry = JSON.parse(storage()?.getItem(key) || 'null') as Entry | undefined; } catch { entry = undefined; }
  }
  if (!usable(entry, scope, name)) {
    forgetBackgroundRead(scope, name);
    return undefined;
  }
  entries.set(key, entry);
  return { data: entry.data as T, at: entry.at };
}

export function forgetBackgroundRead(scope: BackgroundReadScope, name: string): void {
  const key = backgroundReadKey(scope, name);
  entries.delete(key);
  try { storage()?.removeItem(key); } catch { /* Optional cache. */ }
}

function store(scope: BackgroundReadScope, name: string, data: unknown, persist: boolean): void {
  const key = backgroundReadKey(scope, name);
  const entry: Entry = { ...scope, name, at: Date.now(), data };
  entries.delete(key);
  entries.set(key, entry);
  for (const old of [...entries.keys()].slice(0, Math.max(0, entries.size - LIMIT))) entries.delete(old);
  const local = storage();
  if (!local) return;
  try {
    if (!persist) { local.removeItem(key); return; }
    const encoded = JSON.stringify(entry);
    if (encoded.length > MAX_STORED_CHARS) { local.removeItem(key); return; }
    local.setItem(key, encoded);
    // Another account's entries are never read back, so they need not wait for
    // their sign-out; expired ones go too, then the oldest beyond the limit.
    const actorPrefix = `${PREFIX}[${JSON.stringify(scope.actorId)},`;
    const mine: Array<{ key: string; at: number }> = [];
    for (const value of storedKeys(local)) {
      let at = Number.NaN;
      try { at = Number((JSON.parse(local.getItem(value) || 'null') as Entry | null)?.at); } catch { /* Removed below. */ }
      if (!value.startsWith(actorPrefix) || !Number.isFinite(at) || Date.now() - at >= KEEP_MS) local.removeItem(value);
      else mine.push({ key: value, at });
    }
    mine.sort((a, b) => a.at - b.at);
    for (const old of mine.slice(0, Math.max(0, mine.length - LIMIT))) { local.removeItem(old.key); entries.delete(old.key); }
  } catch { /* A full or disabled store must not block the screen. */ }
}

/** `persist: false` keeps an answer in memory only, for data such as pay and
 *  bank details that must never be written to this device's storage. */
export interface BackgroundReadOptions { maxAgeMs?: number; persist?: boolean }

/** Records an answer a screen loaded for itself, so its next visit draws it at once.
 *  Ignored unless the scope's actor is the one signed in on this device. */
export function rememberBackgroundRead(scope: BackgroundReadScope, name: string, data: unknown, { persist = true }: BackgroundReadOptions = {}): void {
  if (currentBackgroundReadScope(scope.workspaceId)?.actorId !== scope.actorId) return;
  store(scope, name, data, persist);
}

/**
 * Answers from the cache while it is younger than `maxAgeMs`, otherwise loads,
 * sharing one request per key. A failed read forgets the entry, so a screen
 * never keeps showing what the server has stopped returning.
 */
export function readInBackground<T>(
  scope: BackgroundReadScope,
  name: string,
  load: () => Promise<T>,
  { maxAgeMs = 0, persist = true }: BackgroundReadOptions = {},
): Promise<T> {
  const cached = peekBackgroundRead<T>(scope, name);
  if (cached && Date.now() - cached.at < maxAgeMs) return Promise.resolve(cached.data);
  const key = backgroundReadKey(scope, name);
  const pending = requests.get(key);
  if (pending) return pending as Promise<T>;
  const started = generation;
  const request = load().then(data => {
    if (generation === started) store(scope, name, data, persist);
    return data;
  }, error => {
    if (generation === started) forgetBackgroundRead(scope, name);
    throw error;
  });
  requests.set(key, request);
  void request.finally(() => { if (requests.get(key) === request) requests.delete(key); }).catch(() => {});
  return request;
}

/** Answers already in flight from before this call can no longer be stored. */
export function clearBackgroundReads(): void {
  generation++;
  entries.clear();
  requests.clear();
  const local = storage();
  try {
    if (local) for (const key of storedKeys(local)) local.removeItem(key);
  } catch { /* Optional cache. */ }
}

if (typeof window !== 'undefined') window.addEventListener('quantis:auth-cleared', clearBackgroundReads);
